import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { InputDatePicker } from '@/components/ui/InputDatePicker'
import { TimePicker } from '@/components/ui/timePicker'
import { useToast } from '@/components/ui/use-toast'
import {
  EstateSettingRoundDatesValues,
  useEstateSettingRoundDatesSchema,
} from '@/estate/hooks/useEstateSettingRoundDatesSchema'
import { EstateForSalePageFragment, useUpdateEstateRoundMutation } from '@gql'
import { yupResolver } from '@hookform/resolvers/yup'
import { DEFAULT_DATE_FORMAT, parseDate } from '@almaris/shared/utils/dates'
import { format } from 'date-fns'
import React, { useEffect, useMemo } from 'react'
import { FieldErrors, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

type EstateSettingRoundDatesProps = {
  round: EstateForSalePageFragment['estateRounds'][number]
  readOnly?: boolean
  canEditStartDate?: boolean
  canEditEndDate?: boolean
}

export const EstateSettingRoundDates = ({
  round,
  readOnly,
  canEditStartDate,
  canEditEndDate,
}: EstateSettingRoundDatesProps) => {
  const { t } = useTranslation()
  const { toast } = useToast()

  const [updateEstateRound] = useUpdateEstateRoundMutation()

  const { schema } = useEstateSettingRoundDatesSchema()

  const startDate = useMemo(() => {
    const defaultStartDate = new Date()
    defaultStartDate.setHours(9, 0, 0, 0)
    return parseDate(round.startDate, DEFAULT_DATE_FORMAT) ?? defaultStartDate
  }, [round])

  const endDate = useMemo(() => {
    const defaultEndDate = new Date()
    defaultEndDate.setHours(18, 0, 0, 0)
    defaultEndDate.setMonth(defaultEndDate.getMonth() + 1)
    return parseDate(round.endDate, DEFAULT_DATE_FORMAT) ?? defaultEndDate
  }, [round])

  const defaultValues = useMemo(() => {
    return {
      startDate,
      endDate,
      startTime: format(startDate, 'HH:mm'),
      endTime: format(endDate, 'HH:mm'),
    }
  }, [endDate, startDate])

  const form = useForm<EstateSettingRoundDatesValues>({
    resolver: yupResolver(schema),
    defaultValues,
  })

  const onSubmit = async ({
    startTime,
    startDate,
    endTime,
    endDate,
  }: EstateSettingRoundDatesValues) => {
    const [startHour, startMinute] = startTime.split(':').map(Number)
    startDate.setHours(startHour, startMinute, 0, 0)

    const [endHour, endMinute] = endTime.split(':').map(Number)
    endDate.setHours(endHour, endMinute, 0, 0)

    await updateEstateRound({
      variables: {
        id: round.id,
        values: {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        },
      },
      onCompleted: () => {
        toast({
          variant: 'success',
          description: t('EstateSettingRoundDates.toast.success'),
        })
      },
      onError: (error) => {
        console.error(error)
        toast({
          variant: 'destructive',
          title: t('EstateSettingRoundDates.updateEstateRound.error.title'),
          description: t(
            'EstateSettingRoundDates.updateEstateRound.error.description'
          ),
        })
      },
    })
  }

  const onInvalid = (errors: FieldErrors<EstateSettingRoundDatesValues>) => {
    console.log({ errors })
  }

  // Reset form when round changes (e.g., when a new round is created)
  useEffect(() => {
    form.reset(defaultValues)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, form.reset, round.id])

  useEffect(() => {
    const { unsubscribe } = form.watch((value, { type }) => {
      if (type !== 'change') return
      form.handleSubmit(onSubmit, onInvalid)()
    })
    return () => unsubscribe()
  }, [form.watch])

  return (
    <Form {...form}>
      <form className="tw-space-y-6 tw-pt-6">
        <div className="tw-flex tw-space-x-4 ">
          <FormField
            control={form.control}
            name="startDate"
            render={({ field }) => (
              <FormItem className="tw-flex tw-flex-col tw-w-full">
                <FormLabel>
                  {t('EstateSettingRoundDates.startDate.date')}
                </FormLabel>
                <InputDatePicker
                  {...field}
                  fromYear={1900}
                  toYear={2100}
                  disabled={readOnly || !canEditStartDate}
                />
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="startTime"
            render={({ field }) => (
              <FormItem className="tw-flex tw-flex-col tw-w-full">
                <FormLabel>
                  {t('EstateSettingRoundDates.startDate.hour')}
                </FormLabel>
                {/* TODO make a component with InputDatePicker that works with dates */}
                <TimePicker
                  {...field}
                  disabled={readOnly || !canEditStartDate}
                />
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="tw-flex tw-space-x-4">
          <FormField
            control={form.control}
            name="endDate"
            render={({ field }) => (
              <FormItem className="tw-flex tw-flex-col tw-w-full">
                <FormLabel>
                  {t('EstateSettingRoundDates.endDate.date')}
                </FormLabel>
                <InputDatePicker
                  {...field}
                  fromYear={1900}
                  toYear={2100}
                  disabled={readOnly || !canEditEndDate}
                />
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="endTime"
            render={({ field }) => (
              <FormItem className="tw-flex tw-flex-col tw-w-full">
                <FormLabel>
                  {t('EstateSettingRoundDates.endDate.hour')}
                </FormLabel>
                {/* TODO make a component with InputDatePicker that works with dates */}
                <TimePicker {...field} disabled={readOnly || !canEditEndDate} />
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </form>
    </Form>
  )
}
